import React, { useRef, useState } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { Icon } from "../../components/Component";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import dayjs from "dayjs";
import { formatCurrency } from "../../utils/Utils";

interface RABillPdfGeneratorProps {
  raBillData: any;
  raBillLines: any[];
  deductions: any[];
  totals: any;
  orgLogo?: string;
  orgName?: string;
  getPdfUrl?: (blob: Blob) => void;
  triggerAttribute?: string;
  onPdfGenerated?: () => void;
}

const RABillPdfGenerator: React.FC<RABillPdfGeneratorProps> = ({
  raBillData,
  raBillLines,
  deductions,
  totals,
  orgLogo,
  orgName = "Your Organization",
  getPdfUrl,
  triggerAttribute,
  onPdfGenerated,
}) => {
  const componentRef = useRef<HTMLDivElement>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState(false);

  const generatePDF = async (download: boolean = false) => {
    console.log("=== RA Bill PDF Generation Started ===");
    console.log("raBillData:", raBillData);
    console.log("raBillLines:", raBillLines?.length, "lines");
    console.log("componentRef.current:", componentRef.current);

    if (!componentRef.current) {
      console.error("componentRef.current is null!");
      return;
    }

    try {
      setIsGenerating(true);

      const element = componentRef.current;

      console.log("Element dimensions:", {
        offsetWidth: element.offsetWidth,
        offsetHeight: element.offsetHeight,
        scrollWidth: element.scrollWidth,
        scrollHeight: element.scrollHeight,
        innerHTML_length: element.innerHTML.length,
      });

      // Wait for any pending renders
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Use html2canvas to capture the component
      console.log("Starting html2canvas...");
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: true,
        onclone: (clonedDoc, clonedElement) => {
          console.log("onclone called, clonedElement:", {
            offsetWidth: clonedElement.offsetWidth,
            offsetHeight: clonedElement.offsetHeight,
          });

          // Ensure the cloned element is visible
          clonedElement.style.display = "block";
          clonedElement.style.visibility = "visible";
          clonedElement.style.position = "relative";
          clonedElement.style.left = "0";
          clonedElement.style.top = "0";

          const style = clonedDoc.createElement("style");
          style.innerHTML = `
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            [data-pdf-template] {
              display: block !important;
              visibility: visible !important;
            }
          `;
          clonedDoc.head.appendChild(style);
        },
      });

      console.log("html2canvas completed. Canvas dimensions:", {
        width: canvas.width,
        height: canvas.height,
      });

      // Create PDF
      const pdf = new jsPDF("p", "mm", "a4");

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // Calculate dimensions
      const marginTop = 8;
      const marginBottom = 10;
      const marginSide = 5;
      const contentWidth = pdfWidth - marginSide * 2;
      const contentHeight = pdfHeight - marginTop - marginBottom;

      // Convert canvas dimensions to PDF dimensions
      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * contentWidth) / canvas.width;

      // Calculate total pages needed - use floor and check remainder
      // Only add extra page if there's significant content (more than 50 pixels)
      const minContentThreshold = 50; // minimum pixels to warrant a new page
      const basePages = Math.floor(imgHeight / contentHeight);
      const remainingHeight = imgHeight - (basePages * contentHeight);
      const remainingPixels = (remainingHeight * canvas.width) / contentWidth;
      const totalPages = remainingPixels > minContentThreshold ? basePages + 1 : Math.max(1, basePages);

      console.log("PDF dimensions:", {
        pdfWidth,
        pdfHeight,
        contentWidth,
        contentHeight,
        imgWidth,
        imgHeight,
        totalPages,
        remainingPixels,
      });

      // Process each page
      for (let page = 0; page < totalPages; page++) {
        console.log(`Processing page ${page + 1} of ${totalPages}`);

        if (page > 0) {
          pdf.addPage();
        }

        // Calculate the portion of the image to show on this page
        const sourceY = (page * contentHeight * canvas.width) / contentWidth;
        const sourceHeight = Math.min(
          (contentHeight * canvas.width) / contentWidth,
          canvas.height - sourceY
        );

        console.log(`Page ${page + 1} - sourceY: ${sourceY}, sourceHeight: ${sourceHeight}`);

        // Only render if there's meaningful content (more than minimum threshold)
        if (sourceHeight > minContentThreshold) {
          // Create temporary canvas for this page
          const pageCanvas = document.createElement("canvas");
          const pageCtx = pageCanvas.getContext("2d");

          if (pageCtx) {
            // Set canvas size
            pageCanvas.width = canvas.width;
            pageCanvas.height = sourceHeight;

            // Fill with white background
            pageCtx.fillStyle = "#ffffff";
            pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

            // Draw the content for this page
            pageCtx.drawImage(
              canvas,
              0,
              sourceY,
              canvas.width,
              sourceHeight,
              0,
              0,
              canvas.width,
              sourceHeight
            );

            // Convert to image data
            const pageImgData = pageCanvas.toDataURL("image/png", 0.95);

            // Calculate PDF dimensions for this page segment
            const pdfContentHeight = (sourceHeight * contentWidth) / canvas.width;

            // Add image to PDF
            console.log(`Adding image to PDF - page ${page + 1}, dimensions: ${contentWidth}x${pdfContentHeight}`);
            pdf.addImage(
              pageImgData,
              "PNG",
              marginSide,
              marginTop,
              contentWidth,
              pdfContentHeight,
              undefined,
              "FAST"
            );
            console.log(`Image added to page ${page + 1}`);
          }
        }

        // Add page number at the bottom
        pdf.setFontSize(10);
        pdf.setTextColor(128, 128, 128);
        pdf.text(
          `Page ${page + 1} of ${totalPages}`,
          pdfWidth - 30,
          pdfHeight - 5
        );
      }

      console.log("=== RA Bill PDF Generation Completed ===");

      // Generate blob
      const generatedPdfBlob = pdf.output("blob");
      console.log("PDF Blob size:", generatedPdfBlob.size, "bytes");
      setPdfBlob(generatedPdfBlob);

      // Create URL for preview
      const url = URL.createObjectURL(generatedPdfBlob);
      setPdfUrl(url);

      // Generate blob for email attachment if callback provided
      if (getPdfUrl) {
        getPdfUrl(generatedPdfBlob);
      }

      if (download) {
        // Download the PDF
        pdf.save(
          `RA_Bill_${raBillData.raBillNumber}_${dayjs().format("DDMMYYYY")}.pdf`
        );
      }

      setIsGenerating(false);
      return generatedPdfBlob;
    } catch (error) {
      console.error("Error generating PDF:", error);
      setIsGenerating(false);
      throw error;
    }
  };

  const handlePreview = async () => {
    try {
      await generatePDF(false);
      setShowPreview(true);
      onPdfGenerated?.();
    } catch (error) {
      console.error("Error generating PDF preview:", error);
      onPdfGenerated?.();
    }
  };

  // Helper function to restore body scroll
  const restoreBodyScroll = () => {
    // Use setTimeout to ensure modal has fully closed
    setTimeout(() => {
      // Remove the style attribute entirely to restore to normal state
      document.body.removeAttribute("style");
      document.body.classList.remove("modal-open");
      // Remove any lingering backdrop
      const backdrops = document.querySelectorAll(".modal-backdrop");
      backdrops.forEach((backdrop) => backdrop.remove());
    }, 300);
  };

  const handleDownload = async () => {
    try {
      if (pdfBlob) {
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `RA_Bill_${raBillData.raBillNumber}_${dayjs().format("DDMMYYYY")}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        await generatePDF(true);
      }
      setShowPreview(false);
      restoreBodyScroll();
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  const closePreview = () => {
    setShowPreview(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl("");
    }
    setPdfBlob(null);
    restoreBodyScroll();
  };

  const getStatusColor = (status: string) => {
    const statusColors: any = {
      Draft: "#6c757d",
      Submitted: "#0dcaf0",
      "Pending Approval": "#ffc107",
      Checked: "#fa8c16",
      Approved: "#198754",
      Posted: "#722ed1",
      Paid: "#52c41a",
      Returned: "#dc3545",
    };
    return statusColors[status] || "#6c757d";
  };

  return (
    <>
      {/* PDF Generation Button */}
      <Button
        className="btn btn-white btn-outline-light"
        onClick={handlePreview}
        disabled={isGenerating}
        {...(triggerAttribute ? { [triggerAttribute]: "true" } : {})}
      >
        {isGenerating ? (
          <>
            <div
              className="spinner-border spinner-border-sm me-2"
              role="status"
            >
              <span className="sr-only">Loading...</span>
            </div>
            Generating...
          </>
        ) : (
          <>
            <Icon name="download" />
            <span>Export PDF</span>
          </>
        )}
      </Button>

      {/* PDF Preview Modal */}
      <Modal
        isOpen={showPreview}
        toggle={closePreview}
        size="xl"
        className="modal-dialog-centered"
        backdrop="static"
        scrollable={true}
        onClosed={() => {
          // This runs after modal animation completes - add delay to ensure it's the last thing
          setTimeout(() => {
            document.body.removeAttribute("style");
          }, 100);
        }}
      >
        <ModalHeader toggle={closePreview}>
          <div className="d-flex align-items-center">
            <Icon name="file-docs" className="me-2" />
            RA Bill PDF Preview - {raBillData?.raBillNumber}
          </div>
        </ModalHeader>
        <ModalBody style={{ padding: 0, maxHeight: "70vh", overflow: "auto" }}>
          {pdfUrl && (
            <iframe
              src={pdfUrl}
              width="100%"
              height="600px"
              style={{ border: "none" }}
              title="PDF Preview"
            />
          )}
        </ModalBody>
        <ModalFooter>
          <div className="d-flex justify-content-between w-100">
            <div className="text-muted small">
              <Icon name="info" className="me-1" />
              Preview generated on {dayjs().format("DD-MMM-YYYY HH:mm")}
            </div>
            <div>
              <Button color="primary" onClick={handleDownload}>
                <Icon name="download" className="me-1" />
                Download PDF
              </Button>
              <Button color="secondary" onClick={closePreview} className="ms-2">
                Close
              </Button>
            </div>
          </div>
        </ModalFooter>
      </Modal>

      {/* Hidden PDF Template */}
      <div
        ref={componentRef}
        data-pdf-template="true"
        style={{
          position: "absolute",
          left: "-9999px",
          top: "0",
          width: "210mm",
          minHeight: "297mm",
          padding: "15px",
          backgroundColor: "white",
          fontFamily: "'Arial', sans-serif",
          fontSize: "11px",
          lineHeight: "1.4",
          color: "#333",
          zIndex: -1000,
        }}
      >
        {/* Header */}
        <div
          style={{
            borderBottom: "2px solid #1890ff",
            paddingBottom: "12px",
            marginBottom: "20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            {orgLogo && (
              <img
                src={orgLogo}
                alt={orgName}
                style={{
                  height: "40px",
                  marginRight: "12px",
                }}
              />
            )}
            <div>
              <h1
                style={{
                  color: "#333",
                  margin: "0",
                  fontSize: "18px",
                  fontWeight: "bold",
                }}
              >
                {orgName}
              </h1>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <h2
              style={{
                color: "#1890ff",
                margin: "0 0 4px 0",
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              RA BILL
            </h2>
            <div style={{ fontSize: "12px", fontWeight: "bold" }}>
              {raBillData?.raBillNumber}
            </div>
            <div style={{ fontSize: "10px", color: "#666" }}>
              <span
                style={{
                  display: "inline-block",
                  backgroundColor: getStatusColor(raBillData?.status),
                  color: "white",
                  padding: "2px 8px",
                  borderRadius: "3px",
                  fontSize: "9px",
                }}
              >
                {raBillData?.status === "Submitted" ? "Pending Approval" : raBillData?.status || "N/A"}
              </span>
            </div>
          </div>
        </div>

        {/* Section 1: RA Bill Header Details */}
        <div
          style={{
            marginBottom: "16px",
            pageBreakInside: "avoid",
          }}
        >
          <div
            style={{
              backgroundColor: "#fafafa",
              padding: "8px 12px",
              borderLeft: "3px solid #1890ff",
              marginBottom: "8px",
              fontSize: "13px",
              fontWeight: "bold",
              color: "#333",
            }}
          >
            RA Bill Header Details
          </div>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "10px",
              border: "1px solid #f0f0f0",
            }}
          >
            <tbody>
              <tr>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold", width: "20%" }}>
                  RA Bill Number
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", width: "30%" }}>
                  {raBillData?.raBillNumber || "-"}
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold", width: "20%" }}>
                  Work Order
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", width: "30%" }}>
                  {raBillData?.workOrderNumber || "-"}
                </td>
              </tr>
              <tr>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                  Contractor
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  {raBillData?.contractor || "-"}
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                  Site
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  {raBillData?.site || "-"}
                </td>
              </tr>
              <tr>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                  RA Period
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  {raBillData?.raPeriod?.from && raBillData?.raPeriod?.to ? (
                    <>
                      {dayjs(raBillData.raPeriod.from).format("DD-MMM")} to{" "}
                      {dayjs(raBillData.raPeriod.to).format("DD-MMM-YYYY")}
                    </>
                  ) : "-"}
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                  Status
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      backgroundColor: getStatusColor(raBillData?.status),
                      color: "white",
                      padding: "1px 6px",
                      borderRadius: "3px",
                      fontSize: "9px",
                    }}
                  >
                    {raBillData?.status || "-"}
                  </span>
                </td>
              </tr>
              {(raBillData?.placeOfSupply || raBillData?.taxRegime) && (
                <tr>
                  <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                    Place of Supply
                  </td>
                  <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                    {raBillData?.placeOfSupply || "-"}
                  </td>
                  <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                    Tax Regime
                  </td>
                  <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                    {raBillData?.taxRegime || "-"}
                  </td>
                </tr>
              )}
              {(raBillData?.contractorGstin || raBillData?.companyGstin) && (
                <tr>
                  <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                    Contractor GSTIN
                  </td>
                  <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                    {raBillData?.contractorGstin || "-"}
                  </td>
                  <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                    Company GSTIN
                  </td>
                  <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                    {raBillData?.companyGstin || "-"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Section 2: Commercial Summary */}
        <div
          style={{
            marginBottom: "16px",
            pageBreakInside: "avoid",
          }}
        >
          <div
            style={{
              backgroundColor: "#fafafa",
              padding: "8px 12px",
              borderLeft: "3px solid #1890ff",
              marginBottom: "8px",
              fontSize: "13px",
              fontWeight: "bold",
              color: "#333",
            }}
          >
            Commercial Summary
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
            <div
              style={{
                flex: "1 1 18%",
                minWidth: "100px",
                textAlign: "center",
                padding: "10px",
                border: "1px solid #f0f0f0",
                borderRadius: "4px",
                backgroundColor: "#fff",
              }}
            >
              <div style={{ fontSize: "9px", color: "#666", marginBottom: "4px" }}>WO Total Value</div>
              <div style={{ fontWeight: "bold", fontSize: "11px" }}>
                {formatCurrency(raBillData?.woTotalValue || 0, "INR")}
              </div>
            </div>
            <div
              style={{
                flex: "1 1 18%",
                minWidth: "100px",
                textAlign: "center",
                padding: "10px",
                border: "1px solid #f0f0f0",
                borderRadius: "4px",
                backgroundColor: "#fff",
              }}
            >
              <div style={{ fontSize: "9px", color: "#666", marginBottom: "4px" }}>Cumulative Work Done</div>
              <div style={{ fontWeight: "bold", fontSize: "11px", color: "#52c41a" }}>
                {raBillData?.cumulativeWorkDone || 0}%
              </div>
            </div>
            <div
              style={{
                flex: "1 1 18%",
                minWidth: "100px",
                textAlign: "center",
                padding: "10px",
                border: "1px solid #f0f0f0",
                borderRadius: "4px",
                backgroundColor: "#fff",
              }}
            >
              <div style={{ fontSize: "9px", color: "#666", marginBottom: "4px" }}>Cumulative Work Value</div>
              <div style={{ fontWeight: "bold", fontSize: "11px" }}>
                {formatCurrency(raBillData?.cumulativeWorkValue || 0, "INR")}
              </div>
            </div>
            <div
              style={{
                flex: "1 1 18%",
                minWidth: "100px",
                textAlign: "center",
                padding: "10px",
                border: "1px solid #f0f0f0",
                borderRadius: "4px",
                backgroundColor: "#fff",
              }}
            >
              <div style={{ fontSize: "9px", color: "#666", marginBottom: "4px" }}>Previous RA Bills</div>
              <div style={{ fontWeight: "bold", fontSize: "11px", color: "#1890ff" }}>
                {formatCurrency(raBillData?.previousRABillsValue || 0, "INR")}
              </div>
            </div>
            <div
              style={{
                flex: "1 1 18%",
                minWidth: "100px",
                textAlign: "center",
                padding: "10px",
                border: "1px solid #f0f0f0",
                borderRadius: "4px",
                backgroundColor: "#fff",
              }}
            >
              <div style={{ fontSize: "9px", color: "#666", marginBottom: "4px" }}>Current Bill Value</div>
              <div style={{ fontWeight: "bold", fontSize: "11px", color: "#1890ff" }}>
                {formatCurrency(raBillData?.currentBillValue || 0, "INR")}
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: Work Lines Details */}
        <div
          style={{
            marginBottom: "16px",
            pageBreakInside: "avoid",
          }}
        >
          <div
            style={{
              backgroundColor: "#fafafa",
              padding: "8px 12px",
              borderLeft: "3px solid #1890ff",
              marginBottom: "8px",
              fontSize: "13px",
              fontWeight: "bold",
              color: "#333",
            }}
          >
            Work Lines Details ({raBillLines?.length || 0})
          </div>

          {!raBillLines || raBillLines.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "20px",
                backgroundColor: "#fafafa",
                border: "1px dashed #d9d9d9",
                color: "#999",
                fontSize: "11px",
              }}
            >
              No work lines found
            </div>
          ) : (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "8px",
                pageBreakInside: "auto",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#fafafa" }}>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 4px", textAlign: "center", fontWeight: "bold", width: "5%" }}>Work Line #</th>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 4px", textAlign: "left", fontWeight: "bold", width: "15%" }}>Description</th>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 4px", textAlign: "right", fontWeight: "bold", width: "10%" }}>Contract Qty</th>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 4px", textAlign: "right", fontWeight: "bold", width: "10%" }}>Cum Approved Qty</th>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 4px", textAlign: "right", fontWeight: "bold", width: "10%" }}>Prev Billed Qty</th>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 4px", textAlign: "right", fontWeight: "bold", width: "10%" }}>Available to Bill</th>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 4px", textAlign: "right", fontWeight: "bold", width: "10%" }}>Billed Qty</th>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 4px", textAlign: "right", fontWeight: "bold", width: "8%" }}>Rate</th>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 4px", textAlign: "right", fontWeight: "bold", width: "8%" }}>Subtotal</th>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 4px", textAlign: "center", fontWeight: "bold", width: "6%" }}>GST %</th>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 4px", textAlign: "right", fontWeight: "bold", width: "8%" }}>Line Total</th>
                </tr>
              </thead>
              <tbody>
                {raBillLines.map((line: any, index: number) => (
                  <tr key={index} style={{ pageBreakInside: "avoid" }}>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px", textAlign: "center" }}>{line.workLineNumber}</td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px" }}>
                      <div style={{ fontWeight: "bold" }}>{line.workLineTitle || "-"}</div>
                      {line.workCategory && <div style={{ fontSize: "7px", color: "#666" }}>{line.workCategory}</div>}
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px", textAlign: "right" }}>
                      {line.contractQuantity?.toLocaleString()} {line.uom}
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px", textAlign: "right" }}>
                      {line.cumulativeApprovedQuantity?.toLocaleString()} {line.uom}
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px", textAlign: "right" }}>
                      {line.previousBilledQuantity?.toLocaleString()} {line.uom}
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px", textAlign: "right" }}>
                      {line.availableQuantity?.toLocaleString()} {line.uom}
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px", textAlign: "right", fontWeight: "bold", color: "#1890ff" }}>
                      {line.thisBillQuantity?.toLocaleString()} {line.uom}
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px", textAlign: "right" }}>
                      {formatCurrency(line.rate || 0, "INR")}
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px", textAlign: "right" }}>
                      {formatCurrency(line.lineSubtotal || 0, "INR")}
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px", textAlign: "center" }}>
                      {line.gstPercentage}%
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px", textAlign: "right", fontWeight: "bold", color: "#52c41a" }}>
                      {formatCurrency(line.lineTotal || 0, "INR")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Section 4: Current Bill Summary */}
        {totals && (
          <div
            style={{
              marginBottom: "16px",
              pageBreakInside: "avoid",
            }}
          >
            <div
              style={{
                backgroundColor: "#fafafa",
                padding: "8px 12px",
                borderLeft: "3px solid #1890ff",
                marginBottom: "8px",
                fontSize: "13px",
                fontWeight: "bold",
                color: "#333",
              }}
            >
              Current Bill Summary
            </div>
            <div
              style={{
                padding: "12px",
                border: "1px solid #f0f0f0",
                borderRadius: "4px",
                backgroundColor: "#fff",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", fontSize: "10px" }}>
                <span>Subtotal:</span>
                <strong>{formatCurrency(totals.subtotalExclTax || 0, "INR")}</strong>
              </div>

              {/* GST Slab-wise Breakdown */}
              {totals.gstSlabBreakdown && Object.keys(totals.gstSlabBreakdown).length > 0 &&
                Object.entries(totals.gstSlabBreakdown).map(([rate, amounts]: any) => (
                  <div key={rate} style={{ marginBottom: "6px", fontSize: "9px" }}>
                    <div style={{ color: "#666", marginBottom: "2px" }}>GST @ {rate}%:</div>
                    {amounts.igstRate > 0 ? (
                      <div style={{ display: "flex", justifyContent: "space-between", paddingLeft: "15px" }}>
                        <span>IGST:</span>
                        <span>{formatCurrency(amounts.igstRate, "INR")}</span>
                      </div>
                    ) : (
                      <>
                        <div style={{ display: "flex", justifyContent: "space-between", paddingLeft: "15px" }}>
                          <span>CGST:</span>
                          <span>{formatCurrency(amounts.cgstRate || 0, "INR")}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", paddingLeft: "15px" }}>
                          <span>SGST:</span>
                          <span>{formatCurrency(amounts.sgstRate || 0, "INR")}</span>
                        </div>
                      </>
                    )}
                  </div>
                ))
              }

              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", fontSize: "10px" }}>
                <span><strong>Gross Total:</strong></span>
                <strong style={{ color: "#52c41a" }}>{formatCurrency(totals.grossTotal || 0, "INR")}</strong>
              </div>

              {/* TCS Amount */}
              {totals.tcsAmount > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", fontSize: "10px" }}>
                  <span>TCS Amount ({totals.tcsTaxName} - {totals.tcsRate?.toFixed(2)}%):</span>
                  <strong style={{ color: "#52c41a" }}>+{formatCurrency(totals.tcsAmount, "INR")}</strong>
                </div>
              )}

              {/* Mobilization Interest */}
              {totals.interestCharged && totals.interestAmount > 0 && (
                <div style={{ marginBottom: "6px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "10px" }}>
                    <span>Mobilization Interest ({totals.interestPercentage}% for {totals.interestDays} days):</span>
                    <strong style={{ color: "#52c41a" }}>+{formatCurrency(totals.interestAmount, "INR")}</strong>
                  </div>
                  {totals.interestFromDate && totals.interestToDate && (
                    <div style={{ paddingLeft: "15px", fontSize: "8px", color: "#666" }}>
                      Period: {dayjs(totals.interestFromDate).format("DD-MMM-YYYY")} to {dayjs(totals.interestToDate).format("DD-MMM-YYYY")}
                    </div>
                  )}
                </div>
              )}

              {/* TDS Deduction */}
              {totals.tdsAmount > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", fontSize: "10px" }}>
                  <span>TDS Deduction ({totals.tdsTaxName} - {totals.tdsRate?.toFixed(2)}%):</span>
                  <strong style={{ color: "#dc3545" }}>-{formatCurrency(totals.tdsAmount, "INR")}</strong>
                </div>
              )}

              {/* Payment Deductions */}
              {deductions && deductions.length > 0 && (
                <div style={{ marginBottom: "6px" }}>
                  <div style={{ fontSize: "9px", color: "#666", marginBottom: "4px" }}>Payment Deductions:</div>
                  {deductions.map((deduction: any) => {
                    let displayPercentage = deduction.percentValue;
                    if (deduction.type === "Mobilization") {
                      displayPercentage = deduction.recoveryPercentRate || 0;
                    }
                    return (
                      <div key={deduction.id} style={{ display: "flex", justifyContent: "space-between", paddingLeft: "15px", fontSize: "9px", marginBottom: "2px" }}>
                        <span>{deduction.type}{displayPercentage ? ` (${displayPercentage}%)` : ""}:</span>
                        <span style={{ color: "#dc3545" }}>-{formatCurrency(deduction.amount || 0, "INR")}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Net Payable */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "12px",
                  borderTop: "1px solid #d9d9d9",
                  paddingTop: "8px",
                  marginTop: "8px",
                }}
              >
                <span><strong>Current Bill Total:</strong></span>
                <strong style={{ color: "#1890ff", fontSize: "14px" }}>{formatCurrency(totals.netPayable || 0, "INR")}</strong>
              </div>
            </div>
          </div>
        )}

        {/* Section 5: Payment Information (if available) */}
        {raBillData?.paymentStatus && raBillData.status !== "Draft" && (
          <div
            style={{
              marginBottom: "16px",
              pageBreakInside: "avoid",
            }}
          >
            <div
              style={{
                backgroundColor: "#fafafa",
                padding: "8px 12px",
                borderLeft: "3px solid #1890ff",
                marginBottom: "8px",
                fontSize: "13px",
                fontWeight: "bold",
                color: "#333",
              }}
            >
              Payment Information
            </div>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "10px",
                border: "1px solid #f0f0f0",
              }}
            >
              <tbody>
                <tr>
                  <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold", width: "25%" }}>
                    Payment Status
                  </td>
                  <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", width: "25%" }}>
                    <span
                      style={{
                        display: "inline-block",
                        backgroundColor: raBillData.paymentStatus === "Paid" ? "#52c41a" : raBillData.paymentStatus === "Partially Paid" ? "#fa8c16" : "#dc3545",
                        color: "white",
                        padding: "1px 6px",
                        borderRadius: "3px",
                        fontSize: "9px",
                      }}
                    >
                      {raBillData.paymentStatus}
                    </span>
                  </td>
                  <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold", width: "25%" }}>
                    Amount Paid
                  </td>
                  <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", width: "25%" }}>
                    <strong style={{ color: "#52c41a" }}>{formatCurrency(raBillData.paidAmount || 0, "INR")}</strong>
                  </td>
                </tr>
                {raBillData.lastPaymentDate && (
                  <tr>
                    <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                      Last Payment Date
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                      {dayjs(raBillData.lastPaymentDate).format("DD-MMM-YYYY")}
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                      Payment Mode
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                      {raBillData.paymentMode || "-"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Section 6: Approval Hierarchy */}
        {raBillData?.raBillApprovals && raBillData.raBillApprovals.length > 0 && (
          <div
            style={{
              marginBottom: "16px",
              pageBreakInside: "avoid",
            }}
          >
            <div
              style={{
                backgroundColor: "#fafafa",
                padding: "8px 12px",
                borderLeft: "3px solid #1890ff",
                marginBottom: "8px",
                fontSize: "13px",
                fontWeight: "bold",
                color: "#333",
              }}
            >
              Approval Hierarchy
            </div>

            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "9px",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#fafafa" }}>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 8px", textAlign: "left", fontWeight: "bold", width: "30%" }}>Approver Name</th>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 8px", textAlign: "left", fontWeight: "bold", width: "25%" }}>Role</th>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 8px", textAlign: "center", fontWeight: "bold", width: "20%" }}>Approval Stage</th>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 8px", textAlign: "center", fontWeight: "bold", width: "25%" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {raBillData.raBillApprovals.map((approval: any, index: number) => (
                  <tr key={approval.raBillApprovalId || index}>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px", fontWeight: "bold" }}>
                      {approval.approverName || `User ${approval.userId}` || "-"}
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px" }}>
                      {approval.approverRole || "-"}
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px", textAlign: "center" }}>
                      <span
                        style={{
                          display: "inline-block",
                          backgroundColor: approval.approvalStage === "Approver" ? "#52c41a" : approval.approvalStage === "Checker" ? "#fa8c16" : "#1890ff",
                          color: "white",
                          padding: "1px 6px",
                          borderRadius: "3px",
                          fontSize: "8px",
                        }}
                      >
                        {approval.approvalStage || "-"}
                      </span>
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px", textAlign: "center" }}>
                      <span
                        style={{
                          display: "inline-block",
                          backgroundColor: approval.status === "Approved" ? "#52c41a" : approval.status === "Rejected" ? "#dc3545" : approval.status === "Pending" ? "#fa8c16" : "#6c757d",
                          color: "white",
                          padding: "1px 6px",
                          borderRadius: "3px",
                          fontSize: "8px",
                        }}
                      >
                        {approval.status || "Pending"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer */}
        <div
          style={{
            borderTop: "1px solid #f0f0f0",
            paddingTop: "10px",
            textAlign: "center",
            fontSize: "9px",
            color: "#999",
            marginTop: "20px",
          }}
        >
          Generated on {dayjs().format("DD-MMM-YYYY HH:mm")} | © {dayjs().format("YYYY")} {orgName}
        </div>
      </div>
    </>
  );
};

export default RABillPdfGenerator;
